import React, { useRef, useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { Icons, Menu } from '@nextiva/next-ui-components';

import {
  Item,
  StyledMenuIconButton,
  StyledMenuIcon,
} from 'components/MenuIconButton';
import { FeatureContext } from 'components/AccessResolvers/featureContext';
import { UserContext } from 'containers/pages/ManageUsers/usersContext';
import { findLicenseFeature } from 'utils/license-helpers';
import classes from './userActions.scss';

const linksConst = {
  EDIT_PROFILE: 'editProfile',
  VOICE_SETUP: 'voiceSetup',
  VOICE_SETTINGS: 'voiceSettings',
  CHIME_SETTINGS: 'CHIME_SETTINGS',
  CHIME_SETUP: 'CHIME_SETUP',
};

const anchorOrigin = {
  vertical: 'bottom',
  horizontal: 'left',
};
const transformOrigin = {
  vertical: 'top',
};

const userActions = ({
  user,
  hasVoiceIdentity,
  hasChimeIdentity,
  editAction,
  voiceAction,
  chimeAction,
}) => {
  const {
    featureState: {
      voiceActions: featureToggleVoiceActions,
      isClusterIdInBWPatching,
    },
  } = useContext(FeatureContext);
  const { dispatch } = useContext(UserContext);
  const menuButtonRef = useRef(null);

  const [options, setOptions] = useState([
    {
      label: 'Edit Profile',
      onClick: () => editAction(user),
      value: linksConst.EDIT_PROFILE,
      className: classes.editProfile,
    },
  ]);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [accessChime, setAccessChime] = useState(false);

  useEffect(() => {
    if (user.profiles[0]?.productFeatures) {
      const licenseList = user.profiles[0].productFeatures;

      if (findLicenseFeature(licenseList, 'AMAZON_CHIME_ACCESS')) {
        setAccessChime(true);
      }
    }
  }, []);

  const setOptionsForBWCluster = (items) => {
    if (isClusterIdInBWPatching)
      setOptions(
        items.filter(
          (option) =>
            !(
              option.value === linksConst.VOICE_SETTINGS ||
              option.value === linksConst.VOICE_SETUP
            ),
        ),
      );
    else setOptions(items);
  };

  useEffect(() => {
    if (featureToggleVoiceActions && user.active) {
      switch (true) {
        case hasVoiceIdentity: {
          const option = {
            label: 'Voice Settings',
            value: linksConst.VOICE_SETTINGS,
            onClick: () =>
              voiceAction('settings', 'Voice Settings', user, dispatch),
            className: classes.voiceIcon,
          };

          setOptionsForBWCluster([
            ...options.filter((opt) => opt.value !== linksConst.VOICE_SETUP),
            option,
          ]);
          break;
        }
        default: {
          const option = {
            label: 'Voice Setup',
            value: linksConst.VOICE_SETUP,
            onClick: () => voiceAction('setup', 'Voice Setup', user, dispatch),
            className: classes.voiceIcon,
          };

          setOptionsForBWCluster([...options, option]);
        }
      }
    }
  }, [featureToggleVoiceActions, user.active, hasVoiceIdentity]);

  useEffect(() => {
    if (accessChime) {
      switch (true) {
        case hasChimeIdentity: {
          const option = {
            label: 'Video Settings',
            value: linksConst.CHIME_SETTINGS,
            onClick: () => chimeAction('settings', user),
            className: classes.chimeIcon,
          };

          setOptions((opts) => [
            ...opts.filter((opt) => opt.value !== linksConst.CHIME_SETUP),
            option,
          ]);
          break;
        }
        default: {
          const option = {
            label: 'Video Setup',
            value: linksConst.CHIME_SETUP,
            onClick: () => chimeAction('setup', user),
            className: classes.chimeIcon,
          };

          setOptions((opts) => [...opts, option]);
        }
      }
    }
  }, [accessChime, hasChimeIdentity]);

  return (
    <>
      <StyledMenuIconButton
        onClick={() => setActionsOpen(true)}
        data-testid="PlatformAdminUI-UserList-MenuIconButton"
        aria-label="Menu Icon Button"
        ref={menuButtonRef}
      >
        <StyledMenuIcon icon={Icons.menu} />
      </StyledMenuIconButton>
      <Menu
        open={actionsOpen}
        onClose={() => setActionsOpen(false)}
        anchorEl={menuButtonRef.current}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        disableAutoFocusItem
      >
        {options.map((option) => (
          <Item
            onClick={option.onClick}
            key={option.label}
            data-testid={`PlatformAdminUI-UserList-${option.value}`}
          >
            <div className={option.className}>{option.label}</div>
          </Item>
        ))}
      </Menu>
    </>
  );
};

userActions.propTypes = {
  chimeAction: PropTypes.func,
  editAction: PropTypes.func,
  hasChimeIdentity: PropTypes.bool,
  hasVoiceIdentity: PropTypes.bool,
  user: PropTypes.any,
  voiceAction: PropTypes.func,
};

userActions.defaultProps = {
  user: {},
  hasVoiceIdentity: false,
  hasChimeIdentity: false,
  editAction: () => {},
  voiceAction: () => {},
  chimeAction: () => {},
};

export default userActions;
export const UserActions = React.memo(userActions);
